import type { DataEntry } from 'types/dataEntries';
import type { DrillDownData } from 'types/costViews/drillDown';
import type { ChartType } from 'types/chart'

import { useMemo } from 'react';

import { Legend } from 'shared/Legend';
import { ExportToCsvButton } from 'shared/ExportToCsvButton';
import { LineChart, BarChart, verticalLinePlugin } from 'shared/Chart';
import { SectionFooter } from 'shared/Section';
import { StatusOverlay } from 'shared/Overlay';

import { groupBy as arrayGroupBy } from 'helpers/groupBy';
import { formatCurrency, granularityTooltip } from 'helpers/formatter';

import { useGranularity } from 'hooks/useGranularity';
import { useDrillDownQuery } from 'hooks/useDrillDownQuery';
import { useCumulative } from '../hooks/cumulative';

export interface BreakdownChartProps {
  drillDown: DataEntry<DrillDownData>;
  chartType: ChartType;
  setCurrentStep: (value: string) => void;
}

export const BreakdownChart = (props: BreakdownChartProps) => {
  const [granularityParam] = useGranularity();
  const [cumulative] = useCumulative();
  const granularity = cumulative ? 'day' : granularityParam;

  const { breakdown: groupBy } = useDrillDownQuery();
  const { drillDown, chartType, setCurrentStep } = props;

  const getLabel = useMemo(() => 
    drillDown.status === 'success' ? 
      (name: string) => drillDown.data.alias_map[name] || name : 
      (name: string) => name,
    [drillDown]
  );

  const datasets = useMemo(() => {
    if (drillDown.status !== 'success') {
      return [];
    }

    const entries = Object.entries(arrayGroupBy(drillDown.data.data, groupBy));

    const sortedEntries = entries.map((entry) => entry[1]).sort((a, b) => b.length - a.length)
    const maxLengthData = sortedEntries.length ? sortedEntries[0] : []

    return entries.map(([name, data], index) => {
      const hue: number = 100 * index % 360;
      const color = `hsl(${hue}, 50%, 50%)`;
      const transparentColor = `hsla(${hue}, 50%, 50%, 0.25)`;

      if (data.length && data.length < maxLengthData.length) {
        maxLengthData.map((base, index) => {
          const dataIndex = data[index]
          const fixedIndex = data[0]
          if (dataIndex && dataIndex.date !== base.date) {
            data.splice(index, 0,
              {
                ...fixedIndex,
                date: base.date,
                previous_timeframe_cost: null,
                current_timeframe_cost: null,
                pop_diff: null,
                pop_change: null,
              }
            )
          } else if (!dataIndex) {
            data.push(
              {
                ...fixedIndex,
                date: base.date,
                previous_timeframe_cost: null,
                current_timeframe_cost: null,
                pop_diff: null,
                pop_change: null,
              }
            )
          }
        })
      }

      return {
        label: getLabel(name),
        data,
        borderColor: color,
        backgroundColor: color,
        pointHoverBorderColor: transparentColor,
        name,
      };
    });
  }, [drillDown, groupBy]);

  const series = useMemo(() => datasets.map(({ label, borderColor }) => ({ name: label, color: borderColor })), [datasets]);
  const sortedDataSets = useMemo(() => datasets.sort((a, b) => (b.data[0].current_timeframe_cost) ?? 0  - (a.data[0].current_timeframe_cost ?? 0)), [datasets])
  const sortedIndexMap: {[key: number]: string} = useMemo(() => sortedDataSets.reduce((a, v, index) => ({ ...a, [index]: v.name}), {}), [sortedDataSets]);

  return (
    <div className='relative'>
      <div className='relative h-[320px]'>
        {chartType === 'bar' ? (
          <BarChart
            options={{
              plugins: {
                tooltip: {
                  callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`
                  },
                  itemSort: (a, b) => (b.parsed as any).y - (a.parsed as any).y
                }
              },
              onClick: (evt, activeElements) => {
                const target_points = activeElements.filter((point) =>
                  (evt.x && evt.y && (
                    (evt.x >= point.element.x - ((point.element as any).width as number) / 2) && 
                    (evt.x < point.element.x + ((point.element as any).width as number) / 2) &&
                    (evt.y < point.element.y + ((point.element as any).height as number)) &&
                    (evt.y >= point.element.y)
                  ))
                )
                if (target_points.length) {
                  setCurrentStep(sortedIndexMap[target_points[0].datasetIndex])
                }                
              },
              scales: {
                y: {
                  stacked: true,
                  ticks: {
                    callback: (value: number | string) => formatCurrency(value as number)
                  }
                },
                x: {
                  type: 'time',
                  stacked: true,
                  time: {
                    unit: granularity,
                    displayFormats: {
                      hour: 'hA',
                      day: 'MMM DD',
                      week: 'MMM DD',
                      month: 'MMM YYYY',
                      quarter: 'Qo YYYY',
                      year: 'YYYY'
                    },
                    tooltipFormat: granularityTooltip(granularity),
                  },
                }
              },
              parsing: {
                xAxisKey: 'date',
                yAxisKey: 'current_timeframe_cost'
              }
            }}
            data={{ datasets: sortedDataSets }}
            plugins={verticalLinePlugin}
          />
        ) : (
          <LineChart
            options={{
              plugins: {
                tooltip: {
                  callbacks: {
                    label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`
                  },
                  itemSort: (a, b) => (b.parsed as any).y - (a.parsed as any).y
                }
              },
              onClick: (evt, activeElements) => {
                const target_points = activeElements.filter((point) =>
                  (evt.x && Math.abs(point.element.x - evt.x) <= 20) &&
                  (evt.y && Math.abs(point.element.y - evt.y) <= 20)
                )
                if (target_points.length) {
                  setCurrentStep(sortedIndexMap[target_points[0].datasetIndex])
                }                
              },
              scales: {
                y: {
                  ticks: {
                    callback: (value: number | string) => formatCurrency(value as number)
                  }
                },
                x: {
                  type: 'time',
                  time: {
                    unit: granularity,
                    displayFormats: {
                      hour: 'hA',
                      day: 'MMM DD',
                      week: 'MMM DD',
                      month: 'MMM YYYY',
                      quarter: 'Qo YYYY',
                      year: 'YYYY'
                    },
                    tooltipFormat: granularityTooltip(granularity)
                  },
                }
              },
              parsing: {
                xAxisKey: 'date',
                yAxisKey: 'current_timeframe_cost'
              }
            }}
            data={{ datasets }}
            plugins={verticalLinePlugin}
          />
        )}
      </div>

      <StatusOverlay status={drillDown.status} />

      <SectionFooter className='border-b'>
        <div className='pl-5 mr-5 -ml-5 overflow-x-scroll no-scrollbar'>
          <Legend className='mr-5' series={series} />
        </div>

        {drillDown.status === 'success' && <ExportToCsvButton fileName='drilldown_chart.csv' data={drillDown.data.data} />}
      </SectionFooter>
    </div>
  )
};
